import {
  availableMinutes,
  EXPERIENCE_TIER_LABEL,
  formatDuration,
  type AutofillGap,
  type AutofillPlan,
  type AutofillProposal,
  type AutofillUtilisation,
  type ShiftPlanning,
} from '@reditelji/shared';
import type { PoolVolunteer, TimelinePayload } from '../api/types';
import { briefingStale } from './derive';

/** Stable identity of a proposed piece, so a rejection survives a replan. */
export const gapKey = (g: AutofillGap) => `${g.postId}:${g.fromMin}:${g.toMin}`;

/**
 * Why a proposal is worth a coordinator's eye. Everything else is clean and is
 * accepted in one gesture — the point of the split is that a batch of forty
 * routine assignments should not cost forty decisions.
 */
export type ExceptionKind = 'inexperienced' | 'short-gap' | 'long-day' | 'reconfirm';

export interface ReviewItem {
  key: string;
  proposal: AutofillProposal;
  kinds: ExceptionKind[];
  /** One Slovenian line saying what to weigh. */
  note: string;
}

export interface ReviewSplit {
  exceptions: ReviewItem[];
  clean: AutofillProposal[];
  utilisation: AutofillUtilisation[];
}

/**
 * What the plan asks of each person it still uses. The planner reports this
 * for the whole plan; rejecting a proposal only filters the list, so the
 * numbers are recomputed from what is left rather than left standing.
 */
export function recomputeUtilisation(
  proposals: AutofillProposal[],
  volunteers: PoolVolunteer[],
  planning: ShiftPlanning,
  dayKey: string,
): AutofillUtilisation[] {
  const softDailyMin = Math.min(planning.softDailyMin, planning.maxDailyMin);
  return volunteers
    .map((v) => {
      const mine = proposals.filter((p) => p.volunteerId === v.id);
      const plannedMin = mine.reduce((a, p) => a + (p.gap.toMin - p.gap.fromMin), v.dayMinutes);
      return {
        volunteerId: v.id,
        volunteerName: v.name,
        plannedMin,
        availableMin: availableMinutes(v.availability, dayKey),
        proposedCount: mine.length,
        needsReconfirm: !briefingStale(v),
        overSoft: plannedMin > softDailyMin,
      };
    })
    .filter((u) => u.proposedCount > 0)
    .sort(
      (a, b) => b.plannedMin - a.plannedMin || a.volunteerName.localeCompare(b.volunteerName, 'sl'),
    );
}

const KIND_RANK: Record<ExceptionKind, number> = {
  inexperienced: 0,
  'short-gap': 1,
  'long-day': 2,
  reconfirm: 3,
};

function noteFor(kind: ExceptionKind, p: AutofillProposal, u: AutofillUtilisation | undefined, softDailyMin: number): string {
  switch (kind) {
    case 'inexperienced': {
      const w = p.warnings.find((x) => x.kind === 'inexperienced');
      const who = w ? EXPERIENCE_TIER_LABEL[w.tier] : '';
      return `nujno mesto — nihče izkušen ni bil prost (${who})`;
    }
    case 'short-gap': {
      const min = p.warnings.find((w) => w.kind === 'short-gap')?.min ?? 0;
      return `le ${formatDuration(min)} premora do sosednje izmene`;
    }
    case 'long-day':
      return `dan ${formatDuration(u?.plannedMin ?? 0)} — čez priporočenih ${formatDuration(softDailyMin)}`;
    case 'reconfirm':
      return 'brifing je že poslan — razpored bo potrjeval znova';
  }
}

/**
 * Split the plan into the few proposals that need judgement and the bulk that
 * does not. Exceptions lead with the urgent posts, then by how hard the call
 * is: an urgent post nobody experienced was left for, then a bent turnaround,
 * then a long day, then a retired briefing.
 *
 * A silent edit does not reset anyone's confirmation, so under it a briefing
 * that has already gone out is not something to weigh at all.
 */
export function reviewSplit(
  plan: AutofillPlan,
  volunteers: PoolVolunteer[],
  planning: ShiftPlanning,
  dayKey: string,
  silent: boolean,
): ReviewSplit {
  const softDailyMin = Math.min(planning.softDailyMin, planning.maxDailyMin);
  const utilisation = recomputeUtilisation(plan.proposals, volunteers, planning, dayKey);
  const byVolunteer = new Map(utilisation.map((u) => [u.volunteerId, u]));

  const exceptions: ReviewItem[] = [];
  const clean: AutofillProposal[] = [];
  for (const p of plan.proposals) {
    const u = byVolunteer.get(p.volunteerId);
    const kinds: ExceptionKind[] = [];
    if (p.warnings.some((w) => w.kind === 'inexperienced')) kinds.push('inexperienced');
    if (p.warnings.some((w) => w.kind === 'short-gap')) kinds.push('short-gap');
    if (u?.overSoft) kinds.push('long-day');
    if (u?.needsReconfirm && !silent) kinds.push('reconfirm');
    if (kinds.length === 0) {
      clean.push(p);
      continue;
    }
    exceptions.push({
      key: gapKey(p.gap),
      proposal: p,
      kinds,
      note: kinds.map((k) => noteFor(k, p, u, softDailyMin)).join(' · '),
    });
  }

  exceptions.sort(
    (a, b) =>
      Number(b.proposal.gap.urgent) - Number(a.proposal.gap.urgent) ||
      KIND_RANK[a.kinds[0]!] - KIND_RANK[b.kinds[0]!] ||
      a.proposal.gap.fromMin - b.proposal.gap.fromMin ||
      a.proposal.gap.postCode.localeCompare(b.proposal.gap.postCode, 'sl'),
  );
  return { exceptions, clean, utilisation };
}

export interface PlanHeadline {
  /** Coverage of the day's operating windows, now and once the plan is taken. */
  coverageNow: number;
  coverageAfter: number;
  /** Window minutes the plan covers, and what is left uncovered after it. */
  filledMin: number;
  remainingMin: number;
}

/** Minutes of `range` that fall inside the post's operating windows. */
function inWindows(data: TimelinePayload, gap: AutofillGap): number {
  const post = data.posts.find((p) => p.id === gap.postId);
  if (!post) return 0;
  return post.windows.reduce(
    (a, w) => a + Math.max(0, Math.min(w.toMin, gap.toMin) - Math.max(w.fromMin, gap.fromMin)),
    0,
  );
}

/**
 * The plan as an outcome. Only window minutes count: an open slot a
 * coordinator drew outside a post's opening hours is work, but it is not
 * coverage the board measures.
 */
export function planHeadline(data: TimelinePayload, proposals: AutofillProposal[]): PlanHeadline {
  const { windowMin, gapMin, coveragePct } = data.stats;
  const filledMin = Math.min(
    gapMin,
    proposals.reduce((a, p) => a + inWindows(data, p.gap), 0),
  );
  const remainingMin = Math.max(0, gapMin - filledMin);
  return {
    coverageNow: coveragePct,
    coverageAfter:
      windowMin === 0 ? 100 : Math.round(((windowMin - remainingMin) / windowMin) * 100),
    filledMin,
    remainingMin,
  };
}
